import React from 'react';
import { WaveDivider } from './components/WaveDivider';

export const AboutApproach = ({
  // Hero
  heroHeadline = "Built by Patients, For Patients",
  heroSubheadline = "We didn't just study the healthcare system—we lived it. Here's why we created Real Patients.",
  foundersImage = "/founders-illustrated.png",

  // Problem section
  problemHeadline = "We Know What It's Like",
  problemContent = "To sit in waiting rooms wondering if anyone truly understands. To answer the same questions over and over, yet feel unheard. To watch research happen around you—but rarely with you. We've been there. We are there. And we knew something had to change.",

  // Jen's story
  jenName = "Jen Horonjeff, PhD",
  jenImage = "/jen-portrait.png",
  jenTitle = "Co-founder & CEO",
  jenStory = "I've lived with chronic illness my entire life. Migraines, autoimmune conditions, surgeries—I became a 'professional patient' before I ever chose to be. That experience led me to earn a PhD in patient experience research. I spent years studying why healthcare so often fails to include the people it's meant to serve. The answer was clear: the systems weren't built to listen. Real Patients is my answer to that problem.",
  jenQuote = "Every patient is an expert in their own experience. It's time healthcare started treating us that way.",

  // Ronnie's story
  ronnieName = "Ronnie Sharpe",
  ronnieImage = "/ronnie-portrait.png",
  ronnieTitle = "Co-founder & Chief Patient Officer",
  ronnieStory = "I was diagnosed with cystic fibrosis at birth. For over four decades, I've navigated hospitalizations, clinical trials, and the daily reality of managing a serious chronic condition. I've seen how patient voices can transform care—and how often they're ignored. I co-founded Real Patients because I believe the people living with conditions deserve a seat at the table, not just a checkbox on a form.",
  ronnieQuote = "We're not just building a platform. We're changing who gets to shape the future of healthcare.",

  // Aha moment
  ahaHeadline = "The Moment Everything Changed",
  ahaContent = "We met through patient advocacy work and quickly realized we shared the same frustration: patients had so much to offer—insights, experiences, wisdom earned through years of living with their conditions—but the healthcare system had no good way to capture it. Research studies paid poorly, if at all. Surveys felt impersonal. And the people making decisions about treatments rarely heard directly from the people using them. So we built Real Patients: a platform where your story isn't just collected—it's valued, compensated, and used to create real change.",

  // Beliefs
  belief1Title = "Patients Are Experts",
  belief1Desc = "Your lived experience is irreplaceable knowledge. No textbook, no clinical trial, no algorithm can replicate what you know from living in your body every day.",
  belief2Title = "Your Time Has Value",
  belief2Desc = "Fair compensation isn't optional—it's respect. When we ask for your insights, we pay you fairly for your expertise. Period.",
  belief3Title = "Transparency Is Everything",
  belief3Desc = "HIPAA compliant and fully transparent. You always know how your data is used, who sees it, and what value it creates.",
  belief4Title = "Stories Create Change",
  belief4Desc = "Real experiences shape better treatments, better care, better outcomes. Your story doesn't just matter to you—it matters to everyone who comes after.",

  // Commitments
  commitment1 = "Total transparency: you always know exactly how your data is used",
  commitment2 = "You control what you share, when you share it, and with whom",
  commitment3 = "Fair compensation communicated upfront—before you participate",
  commitment4 = "Your insights reach decision-makers who can create real change",
  commitment5 = "We're patients too—we hold ourselves to the standards we'd want",

  // CTA
  ctaHeadline = "Your Story Matters",
  ctaSubheadline = "Join thousands of patients who are transforming healthcare—one experience at a time.",
  ctaPrimaryText = "Create Your Profile",
  ctaPrimaryLink = "https://apply.savvy.coop/register",
  ctaSecondaryText = "Meet Our Patients",
  ctaSecondaryLink = "#",
  onCtaClick,
  onSecondaryClick
}) => {
  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#272726',
        lineHeight: 1.7,
        overflowX: 'hidden'
      }}
    >
      {/* Section 1: Hero */}
      <section
        style={{
          position: 'relative',
          padding: '120px 20px 100px',
          background: 'linear-gradient(180deg, #faf8f6 0%, #fff 100%)',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        {/* Floating prism decorations */}
        <div style={{ position: 'absolute', top: '15%', left: '5%', width: '100px', height: '70px', background: 'linear-gradient(135deg, #FFE4D9 0%, #FFCBB8 100%)', opacity: 0.25, transform: 'rotate(-12deg)', clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: '25%', right: '8%', width: '80px', height: '55px', background: 'linear-gradient(135deg, #FFCBB8 0%, #FFB4A0 100%)', opacity: 0.2, transform: 'rotate(18deg)', clipPath: 'polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '30%', left: '3%', width: '70px', height: '90px', background: 'linear-gradient(135deg, #B5C9A8 0%, #B5C9A8 100%)', opacity: 0.18, transform: 'rotate(25deg)', clipPath: 'polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '20%', right: '6%', width: '90px', height: '60px', background: 'linear-gradient(135deg, #E8927C 0%, #D4796A 100%)', opacity: 0.15, transform: 'rotate(-20deg)', clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)', zIndex: 0 }} />

        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(40px, 7vw, 64px)',
              fontWeight: '700',
              lineHeight: 1.1,
              marginTop: 0,
              marginBottom: '24px',
              color: '#272726'
            }}
          >
            {heroHeadline}
          </h1>
          <p
            style={{
              fontSize: 'clamp(18px, 2.5vw, 24px)',
              color: '#444',
              maxWidth: '600px',
              margin: '0 auto 40px',
              lineHeight: 1.5
            }}
          >
            {heroSubheadline}
          </p>

          {foundersImage && (
            <div
              style={{
                maxWidth: '500px',
                margin: '0 auto',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
              }}
            >
              <img
                src={foundersImage}
                alt="Founders"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </div>
          )}
        </div>

        {/* Wavy bottom */}
        <svg
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '60px',
            pointerEvents: 'none'
          }}
          viewBox="0 0 1440 60"
          preserveAspectRatio="none"
        >
          <path
            d="M0,30 C200,60 400,0 600,30 C800,60 1000,10 1200,30 C1350,45 1400,25 1440,35 L1440,60 L0,60 Z"
            fill="#ffffff"
          />
        </svg>
      </section>

      {/* Section 2: The Problem We Lived */}
      <section
        style={{
          backgroundColor: '#ffffff',
          padding: '100px 20px',
          textAlign: 'center'
        }}
      >
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 44px)',
              fontWeight: '700',
              marginTop: 0,
              marginBottom: '32px',
              color: '#272726'
            }}
          >
            {problemHeadline}
          </h2>
          <p
            style={{
              fontSize: '20px',
              lineHeight: 1.8,
              color: '#444'
            }}
          >
            {problemContent}
          </p>
        </div>
      </section>

      {/* Section 3: Jen's Story */}
      <section
        style={{
          backgroundColor: '#FAF8F6',
          padding: '100px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Prism decoration */}
        <div style={{ position: 'absolute', top: '10%', left: '2%', width: '80px', height: '50px', background: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)', opacity: 0.15, transform: 'rotate(-15deg)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)', zIndex: 0 }} />

        <div
          style={{
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '60px',
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Photo side */}
          <div className="founder-photo-container" style={{ flex: '1', minWidth: '200px', textAlign: 'center' }}>
            <img
              src={jenImage}
              alt={jenName}
              className="founder-portrait"
              style={{
                width: '280px',
                height: '280px',
                borderRadius: '16px',
                objectFit: 'cover',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              className="founder-portrait"
              style={{
                display: 'none',
                width: '280px',
                height: '280px',
                borderRadius: '16px',
                backgroundColor: '#B5C9A8',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
              }}
            >
              <span style={{ fontSize: '80px', color: 'white', fontFamily: '"Source Serif Pro", serif' }}>J</span>
            </div>
            <h3
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '28px',
                fontWeight: '700',
                marginTop: '24px',
                marginBottom: '4px',
                color: '#272726'
              }}
            >
              {jenName}
            </h3>
            <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>{jenTitle}</p>
          </div>

          {/* Story side */}
          <div style={{ flex: '1.5', minWidth: '250px' }}>
            <p
              style={{
                fontSize: '18px',
                lineHeight: 1.8,
                color: '#333',
                marginTop: 0,
                marginBottom: '32px'
              }}
            >
              {jenStory}
            </p>
            <blockquote
              style={{
                fontSize: '20px',
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontStyle: 'italic',
                color: '#272726',
                borderLeft: '4px solid #EBA09B',
                paddingLeft: '24px',
                margin: 0,
                background: 'linear-gradient(90deg, rgba(235, 160, 155, 0.1) 0%, transparent 100%)',
                padding: '20px 24px',
                borderRadius: '0 8px 8px 0'
              }}
            >
              "{jenQuote}"
            </blockquote>
          </div>
        </div>
      </section>

      {/* Section 4: Ronnie's Story */}
      <section
        style={{
          backgroundColor: '#ffffff',
          padding: '100px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Prism decoration */}
        <div style={{ position: 'absolute', bottom: '15%', right: '3%', width: '90px', height: '55px', background: 'linear-gradient(135deg, #F5B9A5 0%, #EBA09B 100%)', opacity: 0.15, transform: 'rotate(12deg)', clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)', zIndex: 0 }} />

        <div
          style={{
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap-reverse',
            alignItems: 'center',
            gap: '60px',
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Story side */}
          <div style={{ flex: '1.5', minWidth: '250px' }}>
            <p
              style={{
                fontSize: '18px',
                lineHeight: 1.8,
                color: '#333',
                marginTop: 0,
                marginBottom: '32px'
              }}
            >
              {ronnieStory}
            </p>
            <blockquote
              style={{
                fontSize: '20px',
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontStyle: 'italic',
                color: '#272726',
                borderLeft: '4px solid #F5B9A5',
                paddingLeft: '24px',
                margin: 0,
                background: 'linear-gradient(90deg, rgba(245, 185, 165, 0.1) 0%, transparent 100%)',
                padding: '20px 24px',
                borderRadius: '0 8px 8px 0'
              }}
            >
              "{ronnieQuote}"
            </blockquote>
          </div>

          {/* Photo side */}
          <div className="founder-photo-container" style={{ flex: '1', minWidth: '200px', textAlign: 'center' }}>
            <img
              src={ronnieImage}
              alt={ronnieName}
              className="founder-portrait"
              style={{
                width: '280px',
                height: '280px',
                borderRadius: '16px',
                objectFit: 'cover',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              className="founder-portrait"
              style={{
                display: 'none',
                width: '280px',
                height: '280px',
                borderRadius: '16px',
                backgroundColor: '#EBA09B',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
              }}
            >
              <span style={{ fontSize: '80px', color: 'white', fontFamily: '"Source Serif Pro", serif' }}>R</span>
            </div>
            <h3
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '28px',
                fontWeight: '700',
                marginTop: '24px',
                marginBottom: '4px',
                color: '#272726'
              }}
            >
              {ronnieName}
            </h3>
            <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>{ronnieTitle}</p>
          </div>
        </div>
      </section>

      <WaveDivider />

      {/* Section 5: The Moment It Clicked */}
      <section
        style={{
          backgroundColor: '#272726',
          padding: '80px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Prism decorations */}
        <div style={{ position: 'absolute', top: '10%', left: '5%', width: '100px', height: '65px', background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 100%)', opacity: 0.15, transform: 'rotate(-8deg)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '15%', right: '8%', width: '80px', height: '50px', background: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)', opacity: 0.12, transform: 'rotate(15deg)', clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: '50%', right: '3%', width: '60px', height: '40px', background: 'linear-gradient(135deg, #F5D7AA 0%, #F5B9A5 100%)', opacity: 0.1, transform: 'rotate(-20deg)', clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)', zIndex: 0 }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }}>
            {/* Left side - Large quote */}
            <div>
              <div style={{
                fontSize: '120px',
                fontFamily: '"Source Serif Pro", Georgia, serif',
                color: '#EBA09B',
                lineHeight: 0.8,
                marginBottom: '20px',
                opacity: 0.6
              }}>"</div>
              <p style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(24px, 3vw, 32px)',
                fontWeight: '400',
                fontStyle: 'italic',
                color: '#fff',
                lineHeight: 1.5,
                marginTop: '-40px'
              }}>
                Patients had so much to offer—but the healthcare system had no good way to capture it.
              </p>
            </div>

            {/* Right side - Content */}
            <div>
              <p style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', color: '#EBA09B', marginBottom: '12px', fontWeight: '600' }}>The Beginning</p>
              <h2
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: 'clamp(28px, 4vw, 36px)',
                  fontWeight: '600',
                  marginTop: 0,
                  marginBottom: '20px',
                  color: '#fff'
                }}
              >
                {ahaHeadline}
              </h2>
              <p
                style={{
                  fontSize: '16px',
                  lineHeight: 1.8,
                  color: 'rgba(255,255,255,0.8)'
                }}
              >
                We met through patient advocacy and quickly realized we shared the same frustration. Research studies paid poorly, if at all. Patient voices were reduced to data points. The people making decisions about treatments rarely heard directly from the people living with conditions.
              </p>
              <p
                style={{
                  fontSize: '16px',
                  lineHeight: 1.8,
                  color: 'rgba(255,255,255,0.8)',
                  marginTop: '16px'
                }}
              >
                So we built <span style={{ color: '#fff', fontWeight: '600' }}>Real Patients</span>: a platform where your story isn't just collected—it's valued, compensated, and used to create real change.
              </p>
            </div>
          </div>
        </div>

        {/* Responsive layout */}
        <style>{`
          @media (max-width: 768px) {
            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
              gap: 30px !important;
            }
          }
        `}</style>
      </section>

      <WaveDivider flip={true} />

      {/* Section 6: What We Believe */}
      <section
        style={{
          backgroundColor: '#ffffff',
          padding: '100px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* CSS animations */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(var(--rotation, 0deg)); }
            50% { transform: translateY(-10px) rotate(var(--rotation, 0deg)); }
          }
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
            50% { box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
          }
          .belief-card {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            cursor: default;
          }
          .belief-card:hover {
            transform: translateY(-8px) scale(1.02);
            box-shadow: 0 20px 40px rgba(0,0,0,0.12) !important;
          }
          .belief-card:hover .belief-icon {
            transform: scale(1.15) rotate(5deg);
          }
          .belief-icon {
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .belief-number {
            transition: all 0.3s ease;
          }
          .belief-card:hover .belief-number {
            transform: scale(1.1);
            opacity: 0.2;
          }
          @media (max-width: 700px) {
            .beliefs-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>

        {/* Floating decorative elements */}
        <div style={{ position: 'absolute', top: '8%', right: '8%', width: '80px', height: '80px', background: 'linear-gradient(135deg, #F5D7AA 0%, #F5B9A5 100%)', opacity: 0.15, borderRadius: '50%', animation: 'float 6s ease-in-out infinite', '--rotation': '0deg', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '15%', left: '5%', width: '60px', height: '60px', background: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)', opacity: 0.12, borderRadius: '50%', animation: 'float 8s ease-in-out infinite', animationDelay: '-2s', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: '40%', left: '3%', width: '40px', height: '40px', background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 100%)', opacity: 0.1, borderRadius: '50%', animation: 'float 7s ease-in-out infinite', animationDelay: '-4s', zIndex: 0 }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#EBA09B', marginBottom: '16px', fontWeight: '600' }}>Our Values</p>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '600',
                marginTop: 0,
                marginBottom: '16px',
                color: '#272726'
              }}
            >
              What We Believe
            </h2>
            <p style={{ fontSize: '18px', color: '#666', maxWidth: '500px', margin: '0 auto' }}>
              The principles that guide everything we do
            </p>
          </div>

          <div
            className="beliefs-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '28px'
            }}
          >
            {[
              {
                title: belief1Title,
                desc: belief1Desc,
                color: '#EBA09B',
                gradient: 'linear-gradient(135deg, #EBA09B 0%, #D4796A 100%)',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
                    <path d="M15 4l2 2-2 2"/>
                  </svg>
                )
              },
              {
                title: belief2Title,
                desc: belief2Desc,
                color: '#F5B9A5',
                gradient: 'linear-gradient(135deg, #F5B9A5 0%, #EBA09B 100%)',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                )
              },
              {
                title: belief3Title,
                desc: belief3Desc,
                color: '#6B9A7A',
                gradient: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="3"/>
                    <line x1="12" y1="2" x2="12" y2="5"/>
                    <line x1="12" y1="19" x2="12" y2="22"/>
                    <line x1="2" y1="12" x2="5" y2="12"/>
                    <line x1="19" y1="12" x2="22" y2="12"/>
                  </svg>
                )
              },
              {
                title: belief4Title,
                desc: belief4Desc,
                color: '#F5D7AA',
                gradient: 'linear-gradient(135deg, #F5D7AA 0%, #E8927C 100%)',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                    <path d="M8 12h.01M12 12h.01M16 12h.01"/>
                  </svg>
                )
              }
            ].map((belief, index) => (
              <div
                key={index}
                className="belief-card"
                style={{
                  backgroundColor: '#FAF8F6',
                  padding: '36px 32px',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Large background number */}
                <div
                  className="belief-number"
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '10px',
                    fontSize: '120px',
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontWeight: '700',
                    color: belief.color,
                    opacity: 0.08,
                    lineHeight: 1,
                    pointerEvents: 'none'
                  }}
                >
                  {index + 1}
                </div>

                {/* Icon */}
                <div
                  className="belief-icon"
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: belief.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    boxShadow: `0 8px 20px ${belief.color}40`
                  }}
                >
                  {belief.icon}
                </div>

                <h3
                  style={{
                    fontFamily: '"Source Serif Pro", Georgia, serif',
                    fontSize: '22px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: '12px',
                    color: '#272726',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {belief.title}
                </h3>
                <p
                  style={{
                    fontSize: '15px',
                    lineHeight: 1.7,
                    color: '#555',
                    margin: 0,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {belief.desc}
                </p>

                {/* Bottom accent line */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: belief.gradient,
                    opacity: 0.6
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Our Commitment */}
      <section
        style={{
          backgroundColor: '#FAF8F6',
          padding: '100px 20px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* CSS for commitment cards */}
        <style>{`
          .commitment-card {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .commitment-card:hover {
            transform: translateX(8px);
            box-shadow: 0 8px 30px rgba(107, 154, 122, 0.15) !important;
          }
          .commitment-card:hover .commitment-check {
            transform: scale(1.2);
          }
          .commitment-check {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          @keyframes draw-check {
            0% { stroke-dashoffset: 20; }
            100% { stroke-dashoffset: 0; }
          }
          .commitment-card:hover .check-path {
            animation: draw-check 0.4s ease forwards;
          }
          .commitments-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          @media (max-width: 768px) {
            .commitments-grid {
              grid-template-columns: 1fr !important;
            }
            .commitment-last {
              grid-column: span 1 !important;
              max-width: none !important;
            }
          }
          .founder-portrait {
            width: 280px;
            height: 280px;
          }
          @media (max-width: 480px) {
            .founder-portrait {
              width: 200px !important;
              height: 200px !important;
            }
          }
        `}</style>

        {/* Decorative elements */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '120px', height: '120px', border: '2px solid #6B9A7A', opacity: 0.08, borderRadius: '50%', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '15%', left: '3%', width: '80px', height: '80px', border: '2px solid #EBA09B', opacity: 0.06, borderRadius: '50%', zIndex: 0 }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '3px', color: '#6B9A7A', marginBottom: '16px', fontWeight: '600' }}>Our Promise</p>
            <h2
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '600',
                marginTop: 0,
                marginBottom: '16px',
                color: '#272726'
              }}
            >
              Our Commitment to You
            </h2>
            <p style={{ fontSize: '18px', color: '#666', maxWidth: '450px', margin: '0 auto' }}>
              Promises we make—and keep—every day
            </p>
          </div>

          <div className="commitments-grid">
            {[
              { text: commitment1, icon: '👁️' },
              { text: commitment2, icon: '🎛️' },
              { text: commitment3, icon: '💵' },
              { text: commitment4, icon: '📣' },
              { text: commitment5, icon: '❤️' }
            ].map((item, index) => (
              <div
                key={index}
                className={`commitment-card${index === 4 ? ' commitment-last' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  backgroundColor: '#ffffff',
                  padding: '24px 28px',
                  borderRadius: '14px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  cursor: 'default',
                  ...(index === 4 ? { gridColumn: 'span 2', maxWidth: '460px', margin: '0 auto' } : {})
                }}
              >
                {/* Animated check icon */}
                <div
                  className="commitment-check"
                  style={{
                    flexShrink: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(107, 154, 122, 0.3)'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      className="check-path"
                      d="M5 12L10 17L19 7"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ strokeDasharray: 20, strokeDashoffset: 0 }}
                    />
                  </svg>
                </div>

                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: '16px',
                      lineHeight: 1.6,
                      color: '#333',
                      margin: 0,
                      fontWeight: '500'
                    }}
                  >
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 8: CTA */}
      <section
        style={{
          position: 'relative',
          padding: '100px 20px',
          background: 'linear-gradient(180deg, #fff 0%, #FAF8F6 100%)',
          textAlign: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Prism decorations */}
        <div style={{ position: 'absolute', top: '20%', left: '8%', width: '80px', height: '55px', background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 100%)', opacity: 0.15, transform: 'rotate(-10deg)', clipPath: 'polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '25%', right: '10%', width: '70px', height: '45px', background: 'linear-gradient(135deg, #B5C9A8 0%, #6B9A7A 100%)', opacity: 0.12, transform: 'rotate(15deg)', clipPath: 'polygon(0% 0%, 85% 0%, 100% 100%, 15% 100%)', zIndex: 0 }} />

        <div style={{ maxWidth: '600px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2
            style={{
              fontFamily: '"Source Serif Pro", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 44px)',
              fontWeight: '600',
              marginTop: 0,
              marginBottom: '16px',
              color: '#272726'
            }}
          >
            {ctaHeadline}
          </h2>
          <p
            style={{
              fontSize: '18px',
              color: '#666',
              marginBottom: '32px',
              lineHeight: 1.6
            }}
          >
            {ctaSubheadline}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <a
              href={ctaPrimaryLink}
              onClick={(e) => {
                if (onCtaClick) {
                  e.preventDefault();
                  onCtaClick();
                }
              }}
              style={{
                display: 'inline-block',
                backgroundColor: '#272726',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                textDecoration: 'none',
                padding: '14px 32px',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a39'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#272726'}
            >
              {ctaPrimaryText}
            </a>

            {/* Meet Our Patients button - hidden for now
            <a
              href={ctaSecondaryLink}
              onClick={(e) => {
                if (onSecondaryClick) {
                  e.preventDefault();
                  onSecondaryClick();
                }
              }}
              style={{
                display: 'inline-block',
                backgroundColor: 'transparent',
                color: '#272726',
                fontSize: '15px',
                fontWeight: '600',
                textDecoration: 'none',
                padding: '12px 32px',
                border: '2px solid #E8E8E8',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(39, 39, 38, 0.06)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {ctaSecondaryText}
            </a>
            */}
          </div>
        </div>
      </section>
    </div>
  );
};
